import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getJobContacts, getUserContacts, getCompanyContacts, getAllContacts } from '../../../../services/firebase';

const ContactSelector = ({ onSelectContact, jobId, userId, companyId }) => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const selectorRef = useRef(null);
  
  useEffect(() => {
    const fetchContacts = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        let fetchedContacts = [];
        let result;
        
        if (jobId) {
          result = await getJobContacts(jobId);
          if (result.success && result.contacts) {
            fetchedContacts = result.contacts;
          }
        } else if (companyId) {
          result = await getCompanyContacts(companyId);
          if (result.success && result.contacts) {
            fetchedContacts = result.contacts;
          }
        } else if (userId) {
          result = await getUserContacts(userId);
          if (result.success && result.contacts) {
            fetchedContacts = result.contacts;
          }
        } else {
          result = await getAllContacts();
          if (result.success && result.contacts) {
            fetchedContacts = result.contacts;
          }
        }
        
        // If we didn't get contacts from the specific query, try getting all contacts
        if (fetchedContacts.length === 0) {
          result = await getAllContacts();
          if (result.success && result.contacts) {
            fetchedContacts = result.contacts;
          }
        }
        
        // Tag contacts that are specifically relevant to the current context
        fetchedContacts = fetchedContacts.map(contact => ({
          ...contact,
          isContextRelevant: (companyId && contact.companyIds && contact.companyIds.includes(companyId))
        }));
        
        // Sort: put context-relevant first, then alphabetically by name
        fetchedContacts.sort((a, b) => {
          if (a.isContextRelevant && !b.isContextRelevant) return -1;
          if (!a.isContextRelevant && b.isContextRelevant) return 1;
          
          const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
          return aName.localeCompare(bName);
        });
        
        setContacts(fetchedContacts);
        setFilteredContacts(fetchedContacts);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        setError('Failed to load contacts. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchContacts();
  }, [jobId, userId, companyId]);
  
  // Filter contacts based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContacts(contacts);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = contacts.filter(contact => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
      const email = (contact.email || '').toLowerCase();
      const company = (contact.company || '').toLowerCase();
      
      return fullName.includes(query) || 
             email.includes(query) || 
             company.includes(query);
    });
    
    setFilteredContacts(filtered);
  }, [searchQuery, contacts]);
  
  const handleSelectContact = (contact) => {
    onSelectContact(contact);
    setSearchQuery('');
  };

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchFocus = () => {
    setShowResults(true);
  };

  const handleContactClick = (contact) => {
    setSearchQuery(`${contact.firstName || ''} ${contact.lastName || ''}`.trim());
    setShowResults(false);
    onSelectContact(contact);
  };

  // Get signal strength label
  const getSignalStrength = (strength) => {
    switch(strength) {
      case 3: return 'High';
      case 2: return 'Medium';
      case 1: return 'Low';
      default: return '';
    }
  };

  // For debugging
  console.log('Contacts loaded:', contacts.length, 'Filtered:', filteredContacts.length);

  return (
    <div className="relative" ref={selectorRef}>
      <div className="relative rounded-md shadow-sm">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={handleSearchFocus}
          className="block w-full rounded-md border-0 py-1.5 pl-4 pr-10 text-gray-900 dark:text-white bg-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6"
          placeholder="Search contacts by name, email, or company..."
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <svg className="animate-spin h-5 w-5 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          )}
        </div>
      </div>
      
      {showResults && (
        <div className="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-gray-800 shadow-lg max-h-60 overflow-auto ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {isLoading ? (
            <div className="py-2 px-3 text-gray-500 dark:text-gray-400">Loading contacts...</div>
          ) : error ? (
            <div className="py-2 px-3 text-red-500 dark:text-red-400">{error}</div>
          ) : filteredContacts.length === 0 ? (
            <div className="py-2 px-3 text-gray-500 dark:text-gray-400">No contacts found</div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredContacts.map((contact) => (
                <li
                  key={contact.id}
                  onClick={() => handleContactClick(contact)}
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 py-2 px-3"
                >
                  <div className="flex flex-col">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {contact.firstName || ''} {contact.lastName || ''}
                      </span>
                      {contact.signalStrength > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          contact.signalStrength === 3 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : contact.signalStrength === 2 
                              ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300' 
                              : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          Response: {getSignalStrength(contact.signalStrength)}
                        </span>
                      )}
                    </div>
                    {contact.email && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{contact.email}</span>
                    )}
                    {contact.company && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{contact.company}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactSelector; 